import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchPromptFinal,
  fetchSettings,
  updateSettings,
  uploadSizeChart,
} from '@/api/settings';
import { queryKeys } from '@/lib/query-keys';
import type { ProductPhotoUpload, UploadProductPhotoRequest } from '@/types/products';
import type { AsistentePreview, UpdateSettingsRequest } from '@/types/settings';

/**
 * Ajustes generales del negocio activo (módulo 2).
 *
 * El `?? ''` de las funciones no llega a ejecutarse: sin negocio activo la query está
 * deshabilitada (`enabled`) y las mutaciones no se disparan (los botones que las llaman solo
 * existen dentro de una pantalla con negocio activo).
 */

/** `GET /api/tenants/:tenantId/settings`. */
export function useSettings(tenantId: string | null) {
  return useQuery({
    queryKey: queryKeys.settings(tenantId ?? ''),
    queryFn: () => fetchSettings(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
}

/**
 * `PATCH /api/tenants/:tenantId/settings` (solo ADMIN).
 * Al guardar, la respuesta se escribe en la caché (el backend devuelve el objeto completo).
 */
export function useUpdateSettings(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateSettingsRequest) => updateSettings(tenantId ?? '', payload),
    onSuccess: (data) => {
      if (tenantId) queryClient.setQueryData(queryKeys.settings(tenantId), data);
    },
  });
}

/**
 * `POST /api/tenants/:tenantId/products/fotos` (solo ADMIN) — sube la GUÍA DE TALLAS del negocio
 * (Ajustes → General, 24-sep-2026) y devuelve la URL que hay que guardar en `settings.guiaTallasUrl`.
 *
 * No toca ninguna caché: la subida todavía no es parte de los ajustes; el que invalida es el `PATCH` de
 * `settings`, que es el que persiste la URL (mismo criterio que el logo del negocio).
 */
export function useUploadSizeChart(tenantId: string | null) {
  return useMutation({
    mutationFn: (payload: UploadProductPhotoRequest) => uploadSizeChart(tenantId ?? '', payload),
  });
}

/**
 * `GET /api/tenants/:tenantId/settings/prompt-final` — lo que el motor le manda al modelo.
 *
 * Se pide **al abrir el modal** (`enabled`) y sin caché (`staleTime: 0`): enseña lo ÚLTIMO guardado, así
 * que si el usuario tiene cambios sin guardar el propio modal se lo advierte.
 */
export function usePromptFinal(
  tenantId: string | null,
  asistente: AsistentePreview,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.settingsPromptFinal(tenantId ?? '', asistente),
    queryFn: () => fetchPromptFinal(tenantId ?? '', asistente),
    enabled: Boolean(tenantId) && enabled,
    staleTime: 0,
  });
}
